import { useEffect, useMemo, useRef, useState } from 'react'
import type { ModeSupport, SessionMode } from '../core/capabilities'
import { useLibrary } from '../data/useLibrary'
import { fetchServerModel, fetchServerModels, type ServerModel } from '../data/serverModels'
import { useInstances } from '../scene/instances'
import { useDebug } from '../app/debug'

interface LobbyProps {
  onEnter: (mode: SessionMode) => void
  error?: string
  support?: ModeSupport
}

function formatBytes(n?: number): string {
  if (n == null) return ''
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`
  return `${(n / (1024 * 1024)).toFixed(1)} MB`
}

function CubeIcon({ className = '' }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden>
      <path
        d="M12 2.5 21 7v10l-9 4.5L3 17V7l9-4.5Z M3 7l9 4.5M21 7l-9 4.5m0 0V21.5"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function Badge({ ok, label }: { ok?: boolean; label: string }) {
  const tone =
    ok == null
      ? 'border-white/20 text-white/60'
      : ok
        ? 'border-white/40 bg-white/15 text-white'
        : 'border-white/10 bg-black/20 text-white/40'
  return (
    <span className={`rounded-full border px-2.5 py-0.5 text-xs font-medium ${tone}`}>
      {label} {ok == null ? '?' : ok ? '✓' : '—'}
    </span>
  )
}

export function Lobby({ onEnter, error, support }: LobbyProps) {
  const { models, add, remove, reset } = useLibrary()
  const fileInput = useRef<HTMLInputElement>(null)
  const debug = useDebug((s) => s.enabled)
  const setDebug = useDebug((s) => s.setEnabled)

  const [server, setServer] = useState<ServerModel[]>([])
  const [serverLoading, setServerLoading] = useState(true)
  const [adding, setAdding] = useState<ReadonlySet<string>>(new Set())

  useEffect(() => {
    let active = true
    fetchServerModels(import.meta.env.BASE_URL)
      .then((m) => active && setServer(m))
      .finally(() => active && setServerLoading(false))
    return () => {
      active = false
    }
  }, [])

  const imported = useMemo(() => new Set(models.map((m) => m.name)), [models])

  const addServer = async (m: ServerModel) => {
    if (imported.has(m.file) || adding.has(m.file)) return
    setAdding((s) => new Set(s).add(m.file))
    try {
      await add([await fetchServerModel(m)])
    } catch {
      // invalid/unavailable file — it simply won't appear in the library
    } finally {
      setAdding((s) => {
        const next = new Set(s)
        next.delete(m.file)
        return next
      })
    }
  }

  const addAll = () => server.forEach((m) => void addServer(m))
  const pendingServer = server.filter((m) => !imported.has(m.file))

  // Escape hatch: if a stored model ever wedges the app, wipe IndexedDB (and any in-memory spawns)
  // for a clean slate. Server models can be re-added afterwards.
  const resetAll = async () => {
    if (
      !window.confirm('Delete all imported models and reset local storage? This cannot be undone.')
    )
      return
    useInstances.getState().clear()
    await reset()
  }

  return (
    <div className="absolute inset-0 overflow-auto bg-gradient-to-b from-[#ec020f] to-[#b30c1a] text-white">
      <div className="mx-auto flex max-w-5xl flex-col gap-10 px-6 py-10 sm:px-8">
        <header className="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-3">
              <span className="grid h-11 w-11 place-items-center rounded-xl bg-white/15 text-white">
                <CubeIcon className="h-6 w-6" />
              </span>
              <h1 className="bg-gradient-to-r from-white to-red-100 bg-clip-text text-4xl font-semibold tracking-tight text-transparent">
                Kanade's XR Gallery
              </h1>
            </div>
            <p className="max-w-md text-white/85">
              Load models, then step into your headset to place and inspect them in mixed reality.
            </p>
            <div className="flex flex-wrap gap-2 pt-1">
              <Badge ok={support?.ar} label="MR" />
              <Badge ok={support?.vr} label="VR" />
            </div>
          </div>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => onEnter('immersive-ar')}
              disabled={!support?.ar}
              className="rounded-xl bg-white px-6 py-3 font-semibold text-[#b30c1a] shadow-lg shadow-black/20 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-40 disabled:shadow-none"
            >
              Enter MR
            </button>
            <button
              type="button"
              onClick={() => onEnter('immersive-vr')}
              disabled={!support?.vr}
              className="rounded-xl border border-white/40 px-6 py-3 font-medium text-white transition hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Enter VR
            </button>
          </div>
        </header>

        {error && (
          <p className="rounded-xl border border-white/25 bg-black/25 px-4 py-3 text-sm text-white">
            Couldn’t enter session: {error}
          </p>
        )}

        <section className="flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-white/85">
              Server models
            </h2>
            {pendingServer.length > 1 && (
              <button
                type="button"
                onClick={addAll}
                className="text-sm font-medium text-white/90 transition hover:text-white"
              >
                Add all
              </button>
            )}
          </div>

          {serverLoading ? (
            <p className="text-sm text-white/75">Loading…</p>
          ) : server.length === 0 ? (
            <p className="text-sm text-white/75">
              None published. Drop .glb/.gltf into{' '}
              <code className="text-white/80">public/models/</code> and run{' '}
              <code className="text-white/80">npm run models</code>.
            </p>
          ) : (
            <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {server.map((m) => {
                const done = imported.has(m.file)
                const busy = adding.has(m.file)
                return (
                  <li
                    key={m.file}
                    className="flex items-center gap-3 rounded-xl border border-white/15 bg-black/20 p-3"
                  >
                    <span className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-white/10 text-white/70">
                      <CubeIcon className="h-5 w-5" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-medium" title={m.name}>
                        {m.name}
                      </p>
                      <p className="text-xs text-white/50">{formatBytes(m.size)}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => void addServer(m)}
                      disabled={done || busy}
                      className="shrink-0 rounded-lg border border-white/30 px-3 py-1.5 text-sm text-white transition hover:border-white/60 hover:bg-white/10 disabled:opacity-50"
                    >
                      {done ? 'Added' : busy ? 'Adding…' : 'Add'}
                    </button>
                  </li>
                )
              })}
            </ul>
          )}
        </section>

        <section className="flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-white/85">
              Your library {models.length > 0 && `(${models.length})`}
            </h2>
            <input
              ref={fileInput}
              type="file"
              accept=".glb,.gltf,model/gltf-binary,model/gltf+json"
              multiple
              className="hidden"
              onChange={(e) => {
                if (e.target.files) void add(e.target.files)
                e.target.value = ''
              }}
            />
            <button
              type="button"
              onClick={() => fileInput.current?.click()}
              className="text-sm font-medium text-white/90 transition hover:text-white"
            >
              Import .glb / .gltf
            </button>
          </div>

          {models.length === 0 ? (
            <div className="rounded-xl border border-dashed border-white/25 bg-black/20 px-4 py-10 text-center text-white/75">
              Nothing imported yet — add a server model above or import your own.
            </div>
          ) : (
            <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
              {models.map((m) => (
                <li
                  key={m.id}
                  className="group flex flex-col gap-2 rounded-xl border border-white/15 bg-black/20 p-3"
                >
                  <span className="grid aspect-square w-full place-items-center rounded-lg bg-white/5 text-white/50">
                    <CubeIcon className="h-8 w-8" />
                  </span>
                  <span className="truncate text-sm font-medium" title={m.name}>
                    {m.name}
                  </span>
                  <button
                    type="button"
                    onClick={() => void remove(m.id)}
                    className="self-start text-xs text-white/60 transition hover:text-white"
                  >
                    Delete
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>

        <details className="rounded-xl border border-white/15 bg-black/20 text-sm text-white/70">
          <summary className="cursor-pointer select-none px-4 py-3 font-medium text-white">
            Diagnostics
          </summary>
          <div className="flex flex-col gap-3 border-t border-white/15 px-4 py-3">
            <label className="inline-flex cursor-pointer select-none items-center gap-2">
              <input
                type="checkbox"
                checked={debug}
                onChange={(e) => setDebug(e.target.checked)}
                className="h-4 w-4 accent-[#b30c1a]"
              />
              Debug overlays in-headset
            </label>
            <pre className="overflow-x-auto rounded-lg border border-white/15 bg-black/25 px-3 py-2 text-xs text-white/80">
              {`immersive-ar supported: ${support?.ar ?? '?'}\n`}
              {`immersive-vr supported: ${support?.vr ?? '?'}\n`}
              {`UA: ${typeof navigator !== 'undefined' ? navigator.userAgent : '?'}`}
            </pre>
            <div className="flex items-center justify-between gap-3 rounded-lg border border-white/20 bg-black/20 px-3 py-2">
              <span className="text-xs text-white/70">
                Reset local storage if a bad model wedges the app.
              </span>
              <button
                type="button"
                onClick={() => void resetAll()}
                className="shrink-0 rounded-lg border border-white/30 px-3 py-1.5 text-sm text-white transition hover:bg-black/40"
              >
                Reset all data
              </button>
            </div>
          </div>
        </details>
      </div>
    </div>
  )
}
